import { create } from "zustand";
import type { TripLeg } from "@poi-planner/shared";
import { api } from "../services/api";
import { trackGranularRequest } from "../services/granularSaveBarrier";
// tripStore imports this module too, but both directions only reach across
// inside function bodies, so the cycle never runs at module-evaluation time.
import { recordUndoableLegChange, useTripStore } from "./tripStore";

/**
 * Trip legs: named, possibly overlapping date ranges.
 *
 * Legs are GRANULAR data — every mutation is its own request, exactly like
 * pinning in tripStore, and none of it flows through the debounced full-trip
 * autosave. That is deliberate: leg edits by one collaborator must not be
 * clobbered by another client's stale snapshot save. Mutations are optimistic
 * with rollback so the calendar band feels immediate while dragging.
 */
interface LegState {
  legs: TripLeg[];
  error: string | null;

  // Seeded by tripStore.loadTrip from the GET /api/trips/{id} payload's
  // top-level `legs`. There is deliberately no second load path here: two
  // fetches of the same data can disagree.
  setLegs: (legs: TripLeg[]) => void;
  reset: () => void;

  createLeg: (
    tripId: string,
    input: { name: string; startDate: string; endDate: string; color?: number },
  ) => Promise<TripLeg | null>;
  updateLeg: (
    tripId: string,
    legId: string,
    updates: {
      name?: string;
      startDate?: string;
      endDate?: string;
      color?: number;
    },
  ) => Promise<void>;
  deleteLeg: (tripId: string, legId: string) => Promise<void>;
  addPoisToLeg: (
    tripId: string,
    legId: string,
    poiIds: string[],
  ) => Promise<boolean>;
  removePoiFromLeg: (
    tripId: string,
    legId: string,
    poiId: string,
  ) => Promise<void>;
  setPoiLegMembership: (
    tripId: string,
    poiId: string,
    legIds: string[],
  ) => Promise<boolean>;
  /** Mirror the database cascade after a POI is unpinned. */
  stripPoiFromLegs: (poiId: string) => void;
  /** Undo a stripPoiFromLegs when the unpin it mirrored failed. */
  restorePoiToLegs: (poiId: string, legIds: string[]) => void;
  /** Dismiss the surfaced error banner. */
  clearError: () => void;
}

const getInitialState = () => ({
  legs: [] as TripLeg[],
  error: null as string | null,
});

/** Replace one leg in place, preserving list order. */
const replaceLeg = (legs: TripLeg[], next: TripLeg) =>
  legs.map((leg) => (leg.id === next.id ? next : leg));

/**
 * Every leg mutation bumps trips.revision. The cached trip has to learn the
 * new value or the next debounced full autosave sends a stale revision, gets a
 * 409, and silently discards the user's unsaved schedule edits on reload.
 */
const applyRevision = (tripId: string, revision: number) => {
  useTripStore.getState().applyGranularRevision(tripId, revision);
};

export const useLegStore = create<LegState>((set, get) => ({
  ...getInitialState(),

  setLegs: (legs) => set({ legs, error: null }),

  reset: () => set(getInitialState()),

  createLeg: async (tripId, input) => {
    try {
      const { leg, revision } = await trackGranularRequest(
        tripId,
        api.createTripLeg(tripId, input),
      );
      applyRevision(tripId, revision);
      set((state) => ({ legs: [...state.legs, leg], error: null }));
      return leg;
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : "Failed to create leg",
      });
      return null;
    }
  },

  updateLeg: async (tripId, legId, updates) => {
    const previous = get().legs.find((leg) => leg.id === legId);
    if (!previous) return;

    // Optimistic: dragging a bar fires many of these in a row. Each commit is
    // one undo entry — drag and resize commit once per gesture, not per tick.
    recordUndoableLegChange(`Updated leg "${previous.name}"`, () => {
      set((state) => ({
        legs: replaceLeg(state.legs, { ...previous, ...updates }),
      }));
    });

    try {
      const { leg, revision } = await trackGranularRequest(
        tripId,
        api.updateTripLeg(tripId, legId, updates),
      );
      applyRevision(tripId, revision);
      set((state) => ({ legs: replaceLeg(state.legs, leg), error: null }));
    } catch (error) {
      set((state) => ({
        legs: replaceLeg(state.legs, previous),
        error: error instanceof Error ? error.message : "Failed to update leg",
      }));
    }
  },

  deleteLeg: async (tripId, legId) => {
    const previous = get().legs;
    set({ legs: previous.filter((leg) => leg.id !== legId) });

    try {
      applyRevision(
        tripId,
        await trackGranularRequest(tripId, api.deleteTripLeg(tripId, legId)),
      );
      // The server scrubbed leg_ids off every note; mirror it locally so the
      // next full autosave does not write the dangling id straight back.
      useTripStore.getState().stripLegFromNotes(legId);
      set({ error: null });
    } catch (error) {
      set({
        legs: previous,
        error: error instanceof Error ? error.message : "Failed to delete leg",
      });
    }
  },

  addPoisToLeg: async (tripId, legId, poiIds) => {
    const previous = get().legs.find((leg) => leg.id === legId);
    if (!previous) return false;

    const merged = [...new Set([...previous.poiIds, ...poiIds])];
    if (merged.length === previous.poiIds.length) return true;
    set((state) => ({
      legs: replaceLeg(state.legs, { ...previous, poiIds: merged }),
    }));

    try {
      const { leg, revision } = await trackGranularRequest(
        tripId,
        api.addPoisToLeg(tripId, legId, poiIds),
      );
      applyRevision(tripId, revision);
      set((state) => ({ legs: replaceLeg(state.legs, leg), error: null }));
      return true;
    } catch (error) {
      set((state) => ({
        legs: replaceLeg(state.legs, previous),
        error:
          error instanceof Error ? error.message : "Failed to add to the leg",
      }));
      return false;
    }
  },

  removePoiFromLeg: async (tripId, legId, poiId) => {
    const previous = get().legs.find((leg) => leg.id === legId);
    if (!previous) return;

    set((state) => ({
      legs: replaceLeg(state.legs, {
        ...previous,
        poiIds: previous.poiIds.filter((id) => id !== poiId),
      }),
    }));

    try {
      const { leg, revision } = await trackGranularRequest(
        tripId,
        api.removePoiFromLeg(tripId, legId, poiId),
      );
      applyRevision(tripId, revision);
      set((state) => ({ legs: replaceLeg(state.legs, leg), error: null }));
    } catch (error) {
      set((state) => ({
        legs: replaceLeg(state.legs, previous),
        error:
          error instanceof Error
            ? error.message
            : "Failed to remove from the leg",
      }));
    }
  },

  setPoiLegMembership: async (tripId, poiId, legIds) => {
    const previous = get().legs;
    const selected = new Set(legIds);
    set({
      legs: previous.map((leg) => ({
        ...leg,
        poiIds: selected.has(leg.id)
          ? [...new Set([...leg.poiIds, poiId])]
          : leg.poiIds.filter((id) => id !== poiId),
      })),
    });

    try {
      const result = await trackGranularRequest(
        tripId,
        api.setPoiLegMembership(tripId, poiId, legIds),
      );
      applyRevision(tripId, result.revision);
      const persisted = new Set(result.legIds);
      set((state) => ({
        legs: state.legs.map((leg) => ({
          ...leg,
          poiIds: persisted.has(leg.id)
            ? [...new Set([...leg.poiIds, poiId])]
            : leg.poiIds.filter((id) => id !== poiId),
        })),
        error: null,
      }));
      return true;
    } catch (error) {
      set({
        legs: previous,
        error:
          error instanceof Error
            ? error.message
            : "Failed to update leg membership",
      });
      return false;
    }
  },

  stripPoiFromLegs: (poiId) =>
    set((state) => ({
      legs: state.legs.map((leg) =>
        leg.poiIds.includes(poiId)
          ? { ...leg, poiIds: leg.poiIds.filter((id) => id !== poiId) }
          : leg,
      ),
    })),

  restorePoiToLegs: (poiId, legIds) => {
    const restore = new Set(legIds);
    set((state) => ({
      legs: state.legs.map((leg) =>
        restore.has(leg.id) && !leg.poiIds.includes(poiId)
          ? { ...leg, poiIds: [...leg.poiIds, poiId] }
          : leg,
      ),
    }));
  },

  clearError: () => set({ error: null }),
}));

/** Legs covering a given date, in list order. */
export function legsCoveringDate(legs: TripLeg[], date: string): TripLeg[] {
  return legs.filter((leg) => leg.startDate <= date && date <= leg.endDate);
}

/** poiId -> legIds, for the sidebar's leg filter facet. */
export function buildLegPoiMap(legs: TripLeg[]): Map<string, string[]> {
  const map = new Map<string, string[]>();
  for (const leg of legs) {
    for (const poiId of leg.poiIds) {
      const existing = map.get(poiId);
      if (existing) existing.push(leg.id);
      else map.set(poiId, [leg.id]);
    }
  }
  return map;
}
